import { EMPLOYER_TAX_LABEL } from '../../shared/employer-tax.js';
import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// ─── Reports → P&L (owner-only) ───────────────────────────────────────────
// CJ 2026-07-22. The per-café prime-cost P&L for any selected period, on screen
// with a PDF download — no longer welded to the payroll run. Labor from punches
// (straight-time estimate for custom ranges), sales/COGS from Tally. Backend:
// /api/admin/pnl. Both COGS and the employer tax are estimates (labeled).

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

// Local calendar date → YYYY-MM-DD (browser is in ET; no UTC shift).
function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function presetRanges() {
  const now = new Date();
  const day = now.getDay(); // 0=Sun … 6=Sat
  const monThis = new Date(now); monThis.setDate(now.getDate() + (day === 0 ? -6 : 1 - day));
  const sunThis = new Date(monThis); sunThis.setDate(monThis.getDate() + 6);
  const monLast = new Date(monThis); monLast.setDate(monThis.getDate() - 7);
  const sunLast = new Date(monLast); sunLast.setDate(monLast.getDate() + 6);
  const mtd = new Date(now.getFullYear(), now.getMonth(), 1);
  return [
    ['This week', localYmd(monThis), localYmd(sunThis)],
    ['Last week', localYmd(monLast), localYmd(sunLast)],
    ['Month to date', localYmd(mtd), localYmd(now)],
  ];
}

export function PnlReportView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const PR = presetRanges();
  const [from, setFrom] = useState(PR[0][1]);
  const [to, setTo] = useState(PR[0][2]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  async function load(f = from, t = to) {
    setLoading(true); setErr('');
    try {
      const r = await api('GET', `/api/admin/pnl?from=${f}&to=${t}`, null, headers);
      setData(r);
    } catch (e) { setErr(String(e?.message || e).slice(0, 200)); setData(null); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function applyPreset(f, t) { setFrom(f); setTo(t); load(f, t); }

  async function downloadPdf() {
    try {
      const h = {};
      const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('monarchIdToken') : null;
      if (token) h['Authorization'] = `Bearer ${token}`;
      if (currentUser?.id) h['x-user-id'] = String(currentUser.id);
      const res = await fetch(`/api/admin/pnl/pdf?from=${from}&to=${to}`, { headers: h });
      if (!res.ok) throw new Error(await res.text());
      const url = URL.createObjectURL(await res.blob());
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert('Could not download the P&L PDF: ' + (e?.message || e)); }
  }

  const rows = data?.pnl || [];
  const hasSales = rows.some((r) => r.net_sales != null);
  const spec = [];
  if (hasSales) spec.push(['Net Sales', 'top', (r) => money(r.net_sales)]);
  spec.push(['', 'gap']);
  spec.push(['Payroll', 'section']);
  spec.push(['Wages', 'item', (r) => money(r.wages)]);
  spec.push(['Employer Taxes (est.)', 'item', (r) => money(r.employer_tax)]);
  spec.push(['Total Labor', 'subtotal', (r) => money(r.total_labor)]);
  if (hasSales) {
    spec.push(['Net Margin', 'margin', (r) => money(r.net_margin)]);
    spec.push(['', 'gap']);
    spec.push(['COGS', 'section']);
    spec.push(['Theoretical COGS', 'item', (r) => money(r.theoretical_cogs)]);
    spec.push(['covers % of sales', 'memo', (r) => pct(r.costed_pct)]);
    spec.push(['Reported Waste', 'item', (r) => money(r.waste)]);
    spec.push(['Reported Shift Meals', 'item', (r) => money(r.shift_meals)]);
    spec.push(['Total COGS', 'subtotal', (r) => money(r.total_cogs)]);
    spec.push(['Net Prime Margin', 'margin', (r) => money(r.net_prime_margin)]);
    spec.push(['Operating Supplies', 'item', (r) => money(r.operating_supplies)]);
    const hasOverhead = rows.some((r) => (r.total_overhead || 0) > 0);
    spec.push(['After-Supplies Margin', hasOverhead ? 'margin' : 'bottom', (r) => money(r.after_supplies_margin)]);
    if (hasOverhead) {
      spec.push(['', 'gap']);
      spec.push(['Fixed Overhead', 'section']);
      spec.push(['Occupancy (rent, CAM, NNN)', 'item', (r) => money(r.occupancy)]);
      spec.push(['Utilities', 'item', (r) => money(r.utilities)]);
      spec.push(['Insurance', 'item', (r) => money(r.insurance)]);
      spec.push(['Taxes, fees & subscriptions', 'item', (r) => money(r.other_fixed)]);
      spec.push(['Total Overhead', 'subtotal', (r) => money(r.total_overhead)]);
      spec.push(['Operating Profit', 'bottom', (r) => money(r.operating_profit)]);
    }
  }

  const styleFor = (kind) => {
    switch (kind) {
      case 'top': return { fontWeight: 700, pad: 8 };
      case 'section': return { fontWeight: 700, pad: 8 };
      case 'item': return { color: '#5a6470', pad: 22 };
      case 'subtotal': return { fontWeight: 700, pad: 14, borderTop: '1px solid #d0d4d9' };
      case 'memo': return { color: '#9e9e9e', pad: 22, small: true };
      case 'margin': return { fontWeight: 700, pad: 8, bg: '#eef1f4' };
      case 'bottom': return { fontWeight: 700, pad: 8, bg: '#212121', color: '#fff' };
      default: return { pad: 8 };
    }
  };
  const cell = (s, extra) => ({ padding: '3px 8px', fontSize: s.small ? 11 : 13, fontWeight: s.fontWeight, color: s.color || '#212121', ...extra });

  const input = 'border border-gray-300 rounded px-2 py-1 text-sm';

  return (
    <div className="space-y-4">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-base font-semibold">Profit &amp; Loss — by café</div>
            <div className="text-xs text-gray-500">Prime-cost P&amp;L for any period. Labor from punches, sales &amp; COGS from Tally.</div>
          </div>
          <button onClick={downloadPdf} className="btn-primary" disabled={!rows.length}>Download PDF</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {PR.map(([name, f, t]) => (
            <button key={name} onClick={() => applyPreset(f, t)}
              className="text-sm"
              style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #d0d4d9',
                       background: (from === f && to === t) ? '#1d5b4c' : '#fff',
                       color: (from === f && to === t) ? '#fff' : '#212121', cursor: 'pointer' }}>
              {name}
            </button>
          ))}
          <span className="text-xs text-gray-400 mx-1">or</span>
          <input className={input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="text-xs text-gray-500">to</span>
          <input className={input} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          <button onClick={() => load()} className="text-sm" style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid #d0d4d9', cursor: 'pointer' }}>Run</button>
        </div>
        {data && !data.tally_ok && (
          <div className="text-xs mt-2" style={{ color: '#b91c1c' }}>Sales &amp; COGS unavailable (Tally offline) — showing labor only.</div>
        )}
        {data?.tally_ok && (data.unmatched || []).length > 0 && (
          <div className="text-xs mt-2" style={{ color: '#b91c1c' }}>
            No Tally sales match for: {data.unmatched.join(', ')} — that café's sales/COGS columns are blank.
            (Location renamed in one system? Check Tally → Stores.)
          </div>
        )}
      </div>

      <div className="card">
        {loading ? <div className="text-sm text-gray-500">Loading…</div>
          : err ? <div className="text-sm" style={{ color: '#b91c1c' }}>{err}</div>
            : !rows.length ? <div className="text-sm text-gray-500">No data for this period.</div>
              : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th></th>
                        {rows.map((r) => (
                          <th key={r.location_id} style={{ textAlign: 'right', padding: '4px 8px', color: '#757575', fontSize: 12, fontWeight: 600 }}>{r.location_name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {spec.map((s, idx) => {
                        if (s[1] === 'gap') return <tr key={idx}><td colSpan={rows.length + 1} style={{ height: 8 }}></td></tr>;
                        const st = styleFor(s[1]);
                        return (
                          <tr key={idx} style={{ background: st.bg || undefined }}>
                            <td style={cell(st, { paddingLeft: st.pad, borderTop: st.borderTop })}>{s[0]}</td>
                            {rows.map((r) => (
                              <td key={r.location_id} style={cell(st, { textAlign: 'right', borderTop: st.borderTop, fontVariantNumeric: 'tabular-nums' })}>
                                {s[2] ? s[2](r) : ''}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="text-xs text-gray-400 mt-3" style={{ lineHeight: 1.4 }}>
                    Labor = punches × pay rate + a {EMPLOYER_TAX_LABEL} composite employer-tax <b>estimate</b> (both the tax and COGS are estimates). Custom periods use straight-time — the OT premium isn't broken out, so the certified weekly payroll run is the exact labor figure. Each café's labor here is its own punches; the payroll report instead splits the certified run across cafés by share of hours, so the two won't match exactly.
                    {hasSales && ' Sales = price × qty (pre-discount/tax), so it runs above the live screen\'s Square net on discount-heavy days; COGS = theoretical (recipe) + reported waste + shift meals, costed items only. Tips excluded (customer-funded). Fixed overhead is a run-rate estimate from the cost config, amortized to the period and allocated per café; Operating Profit = after-supplies margin − overhead.'}
                    {data?.corporate > 0 && <> Corporate / van overhead (not allocated to cafés) this period: {money(data.corporate)}.</>}
                    {data?.as_of && <> Data as of {String(data.as_of).slice(0, 16).replace('T', ' ')}.</>}
                  </div>
                </div>
              )}
      </div>
    </div>
  );
}
